import React from 'react'
import Card from '#app/components/cards/card.tsx'
import { Breadcrumb } from '#app/components/breadcrumb.tsx'

export const handle = {
	breadcrumb: (match: { params: { id: string } }) => (
		<Breadcrumb
			breadcrumbs={[
				{
					to: '/guides',
					labelKey: 'breadcrumbs.guides',
				},
				{
					to: '/guides/attach-canadian-return',
					labelKey: 'breadcrumbs.attachingReturn',
				},
			]}
		/>
	),
}

const ExampleCard = () => {
	return (
		<div className="mx-auto mt-4 flex max-w-5xl flex-col items-center justify-center gap-6 p-4">
			<div className="text-2xl font-bold">Attaching a Canadian Return</div>

			<Card title="Intuit Profiles">
				<div className="rounded-2xl bg-gray-200">
					<div className="bg-white">
						<div className="flex items-center gap-4 py-6">
							<div>
								<h1 className="text-2xl font-semibold">How to create a custom export in Intuit ProFile</h1>
								<p className="text-sm text-gray-500">
									Step-by-step tutorial to setup return pdf for refundr that includes T1 pages, T1183, T1 Summary and
									Invoice
								</p>
							</div>
							<div className="no-print ml-auto">
								<button
									onClick={() => window.print()}
									className="rounded bg-neutral-900 dark:bg-blue-600 dark:hover:bg-blue-700 px-4 py-2 text-white shadow-sm hover:bg-neutral-800"
								>
									Print Guide
								</button>
							</div>
						</div>
					</div>

					<div className="mx-auto max-w-4xl px-3 py-6">
						<section className="mb-6 rounded-lg bg-white p-6 shadow-sm">
							<h2 className="mb-2 text-xl font-semibold">What you'll need</h2>
							<ul className="list-disc space-y-1 pl-5 text-gray-700">
								<li>
									Intuit <strong>ProFile</strong> installed
								</li>
								<li>A T1 tax return opened in ProFile</li>
								<li>About 2–5 minutes to set up your custom print group</li>
							</ul>
						</section>

						<section className="mb-6 rounded-lg bg-white p-6 shadow-sm">
							<h2 className="mb-4 text-xl font-semibold">Step-by-step</h2>

							<ol className="space-y-6">
								<li>
									<h3 className="font-medium">1. Open the Print Settings</h3>
									<p className="mt-1 text-sm text-gray-600">
										Open any T1 return, then go to <strong>File → Print Setup</strong>. You'll see existing print groups
										like "Client Copy" or "CRA Copy".
									</p>
								</li>

								<li>
									<h3 className="font-medium">2. Create a New Custom Print Group</h3>
									<p className="mt-1 text-sm text-gray-600">
										Click <strong>New</strong> (or <em>Add Group</em>), name the group something clear, for example:
									</p>
									<div className="mt-2">
										<code className="inline-block rounded bg-gray-100 px-3 py-1 text-sm">refundr</code>
									</div>
								</li>

								<li>
									<h3 className="font-medium">3. Choose Which Forms to Include</h3>
									<p className="mt-1 text-sm text-gray-600">
										In the new group's <strong>Forms</strong> tab (or "Forms to Print"), select the exact forms below.
										In many ProFile versions the T1 pages appear as grouped items like "T1-1,2" and "T1-3,4,5,6,7,8" —
										pick both.
									</p>

									<div className="mt-3 grid gap-2 sm:grid-cols-2">
										{['T1 — Pages 1 & 2', 'T1 — Pages 3,4,5,6,7,8', 'T1183 — Page 1', 'T1 Summary', 'Invoice'].map(
											label => (
												<label key={label} className="flex items-center gap-3 rounded border bg-gray-50 p-3">
													<input type="checkbox" defaultChecked className="form-checkbox h-5 w-5 text-blue-600" />
													<div className="font-medium">{label}</div>
												</label>
											),
										)}
									</div>

									<p className="mt-3 text-sm text-gray-500">
										Tip: Use the search box in ProFile's Forms list to quickly find each code (e.g., <em>T1183</em>).
									</p>
								</li>

								<li>
									<h3 className="font-medium">4. Set Output Options (Optional)</h3>
									<p className="mt-1 text-sm text-gray-600">
										Choose where the group prints to: <strong>PDF</strong> (recommended for saving/emailing),{' '}
										<strong>Printer</strong>, or <strong>Document Manager</strong>.
									</p>
								</li>

								<li>
									<h3 className="font-medium">5. Save and Use Your Custom Export</h3>
									<p className="mt-1 text-sm text-gray-600">
										Click <strong>OK</strong> or <strong>Apply</strong> to save. When you want to export:
									</p>
									<ul className="mt-2 list-disc pl-5 text-gray-700">
										<li>Open the return</li>
										<li>
											Go to <strong>File → Print</strong>
										</li>
										<li>Select your custom group (eg. "Custom Export – T1 &amp; Summary")</li>
										<li>
											Click <strong>Print</strong> or <strong>Save as PDF</strong>
										</li>
									</ul>
								</li>
							</ol>
						</section>

						<section className="mb-6 rounded-lg bg-white p-6 shadow-sm">
							<h2 className="mb-2 text-xl font-semibold">Extra tips</h2>
							<ul className="list-disc space-y-2 pl-5 text-gray-700">
								<li>
									Make separate groups for different workflows (client copy, CRA copy,{' '}
									<span className="font-bold">refundr</span>) so you don't need to re-select forms each time.
								</li>
								<li>
									If you're unsure which form code you need, open the Forms list in ProFile and use the search field —
									it will show the exact code names used in your version.
								</li>
								<li>
									Consider adding a short internal naming convention:{' '}
									<code className="rounded bg-gray-100 px-2 py-0.5">ClientName - Year - Group</code> so files sort
									nicely.
								</li>
							</ul>
						</section>

						<div className="mb-6">
							<Card title="Example">
								<div className="border border-gray-300">
									<img src="/img/intuit-print.png" />
								</div>
							</Card>
						</div>

						<Card title="Video">
							<div className="border border-gray-300">
								<iframe
									width="100%"
									height="600"
									src="https://www.youtube.com/embed/ypsO6VOeSaQ?si=Ec3WDbOoFfYh_O3B"
									title="YouTube video player"
									frameBorder="0"
									allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
									referrerPolicy="strict-origin-when-cross-origin"
									allowFullScreen
								></iframe>
							</div>
						</Card>
					</div>
				</div>

				<Card.Footer title="Last updated">
					<span>Last updated: Oct 10, 2025</span>
				</Card.Footer>
			</Card>
		</div>
	)
}

export default ExampleCard
